import {
  Button,
  Box,
  Flex,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  PopoverProps,
} from "@chakra-ui/react";
import { usePopoverOpenState } from "../../utils/usePopoverOpenState";
import { MoreIcon } from "../icons/MoreIcon";
import { forwardRef, ReactElement, ReactNode } from "react";

export interface KebabContextualMenuItem {
  id: string;
  text: string;
  icon: ReactElement;
  onClick: () => void;
  isLastItem?: boolean;
  // For items with nested popovers (like Share)
  customComponent?: ReactNode;
}

export interface KebabContextualMenuProps {
  items: KebabContextualMenuItem[];
  backgroundColor?: string;
  menuPlacement?: PopoverProps["placement"];
  iconSize?: string;
  buttonSize?: string;
  borderRadius?: string;
  gutter?: number;
  margin?: string;
  popoverWidth?: string;
  "aria-label"?: string;
}

export function KebabContextualMenu({
  items,
  backgroundColor = "transparent",
  menuPlacement = "bottom",
  iconSize = "20px",
  buttonSize,
  borderRadius = "12px",
  gutter = 4,
  margin,
  popoverWidth = "250px",
  "aria-label": ariaLabel = "More",
}: KebabContextualMenuProps) {
  const { ref } = usePopoverOpenState();

  return (
    <Popover
      variant="filterSmall"
      gutter={gutter}
      isLazy
      placement={menuPlacement}
    >
      <PopoverTrigger>
        <Button
          onClick={(e) => {
            e.stopPropagation();
          }}
          minWidth={undefined}
          size="sm"
          aria-label={ariaLabel}
          background="transparent"
          border="none"
          p={0}
          m={margin ?? 0}
          _hover={{ background: "transparent" }}
          _active={{ background: "transparent" }}
          _focus={{ boxShadow: "none" }}
        >
          <Box
            bg={backgroundColor}
            borderRadius={borderRadius}
            width={buttonSize}
            height={buttonSize}
            p={buttonSize ? undefined : "4px"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="all 0.2s"
            _hover={{
              bg:
                backgroundColor === "transparent"
                  ? "transparent.white.10"
                  : "transparent.white.20",
            }}
          >
            <MoreIcon boxSize={iconSize} color="neutral.300" />
          </Box>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        width={popoverWidth}
        ref={ref}
        variants={{
          enter: { opacity: 1, top: 0 },
          exit: { opacity: 0, top: -20 },
        }}
      >
        <PopoverBody flexDirection="column">
          {items.map(
            (item, index) =>
              item.customComponent || (
                <KebabContextualMenuItem
                  key={item.id}
                  text={item.text}
                  icon={item.icon}
                  onClick={item.onClick}
                  isLastItem={item.isLastItem ?? index === items.length - 1}
                />
              ),
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

interface KebabContextualMenuItemProps {
  text: string;
  onClick: () => void;
  icon: ReactElement;
  isLastItem?: boolean;
}

export const KebabContextualMenuItem = forwardRef<
  HTMLAnchorElement,
  KebabContextualMenuItemProps
>(({ text, icon, onClick, isLastItem = false }, ref) => {
  return (
    <Link
      role="group"
      onClick={(e) => {
        onClick();
        e.preventDefault();
        e.stopPropagation();
      }}
      transition="all .2s linear"
      ref={ref}
    >
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        padding="12px 8px 12px 16px"
        borderBottom={!isLastItem ? "1px solid" : undefined}
        borderColor="transparent.white.10"
        transition="inherit"
      >
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="130%"
          letterSpacing="0.5px"
          color="dune.700"
          transition="inherit"
          _groupHover={{ color: "dune.100" }}
        >
          {text}
        </Text>
        {icon}
      </Flex>
    </Link>
  );
});

KebabContextualMenuItem.displayName = "KebabContextualMenuItem";
